import React, { useState } from "react";
import { FaTimes, FaFileCode, FaSearch } from "react-icons/fa";

const TemplateModal = ({
  templates,
  isOpen,
  onClose,
  onSendTemplate,
  isSending,
}) => {
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [paramValues, setParamValues] = useState({});
  const [search, setSearch] = useState("");

  if (!isOpen) return null;

  const extractBodyText = (components) => {
    if (!components) return "";
    const bodyComp = components.find((c) => c.type === "BODY");
    return bodyComp?.text || "";
  };

  const extractParamCount = (text) => {
    if (!text) return 0;
    const matches = text.match(/\{\{\d+\}\}/g);
    return matches ? new Set(matches).size : 0;
  };

  const handleSelectTemplate = (tmpl) => {
    setSelectedTemplate(tmpl);
    const bodyText = extractBodyText(tmpl.components);
    const count = extractParamCount(bodyText);

    const initialParams = {};
    for (let i = 1; i <= count; i++) {
      initialParams[i] = "";
    }
    setParamValues(initialParams);
  };

  const handleParamChange = (index, value) => {
    setParamValues((prev) => ({ ...prev, [index]: value }));
  };

  const handleSend = () => {
    if (!selectedTemplate) return;

    // Convert paramValues to Meta API component format
    const bodyText = extractBodyText(selectedTemplate.components);
    const count = extractParamCount(bodyText);

    let components = null;
    if (count > 0) {
      const parameters = [];
      for (let i = 1; i <= count; i++) {
        parameters.push({
          type: "text",
          text: paramValues[i] || "",
        });
      }
      components = [
        {
          type: "body",
          parameters: parameters,
        },
      ];
    }

    onSendTemplate({
      template_name: selectedTemplate.name,
      language_code: selectedTemplate.language || "en_US",
      components: components,
    });

    onClose();
  };

  const filteredTemplates = templates.filter((t) =>
    t.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="wa-modal-overlay" onClick={onClose}>
      <div className="wa-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="wa-modal-header">
          <h3>
            <FaFileCode style={{ color: "#00a884" }} /> Select Approved Meta Template
          </h3>
          <button className="wa-modal-close" onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        {/* Body */}
        <div className="wa-modal-body">
          <div className="wa-modal-search">
            <input
              type="text"
              placeholder="Search templates by name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          {filteredTemplates.length === 0 ? (
            <div className="wa-empty-chats">
              <p>No templates found. Click "Sync Templates" to fetch from Meta.</p>
            </div>
          ) : (
            filteredTemplates.map((tmpl) => {
              const bodyText = extractBodyText(tmpl.components);
              const isSelected = selectedTemplate?.id === tmpl.id;

              return (
                <div
                  key={tmpl.id || tmpl.name}
                  className={`wa-template-card ${isSelected ? "selected" : ""}`}
                  onClick={() => handleSelectTemplate(tmpl)}
                >
                  <div className="wa-template-card-header">
                    <span className="wa-template-card-name">{tmpl.name}</span>
                    <div className="wa-template-card-meta">
                      <span className="wa-template-card-tag category">
                        {tmpl.category || "UTILITY"}
                      </span>
                      <span className="wa-template-card-tag language">
                        {tmpl.language || "en_US"}
                      </span>
                    </div>
                  </div>
                  <div className="wa-template-card-body">{bodyText}</div>
                </div>
              );
            })
          )}

          {/* Dynamic parameter inputs */}
          {selectedTemplate && (
            <div className="wa-template-params">
              <h4>Fill Variables for "{selectedTemplate.name}"</h4>
              {Object.keys(paramValues).length === 0 ? (
                <p style={{ fontSize: 13, color: "#667781" }}>
                  This template has no dynamic parameters.
                </p>
              ) : (
                Object.keys(paramValues).map((key) => (
                  <div key={key} className="wa-param-input-group">
                    <label>Variable {`{{${key}}}`}:</label>
                    <input
                      type="text"
                      placeholder={`Value for {{${key}}}`}
                      value={paramValues[key]}
                      onChange={(e) => handleParamChange(key, e.target.value)}
                    />
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="wa-modal-footer">
          <button className="wa-btn wa-btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="wa-btn wa-btn-primary"
            onClick={handleSend}
            disabled={!selectedTemplate || isSending}
          >
            {isSending ? "Sending..." : "Send Template"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TemplateModal;
